import React, { useState, useEffect } from 'react';
import { X, UploadCloud, File, AlertCircle } from 'lucide-react';
import api from '../../utils/api';

const predefinedCategories = ['Image', 'Video', 'Audio', 'Document', 'Brochure', 'Price List', 'Other'];

const MediaUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [category, setCategory] = useState('');
  
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setDisplayName('');
      setCategory('');
      setError('');
    }
  }, [isOpen]);

  const validateFile = (selectedFile) => {
    setError('');
    if (!selectedFile) return false;

    const size = selectedFile.size;
    const type = selectedFile.type;

    if (type.startsWith('image/') && size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB for WhatsApp.');
      return false;
    }
    if (type.startsWith('video/') && size > 16 * 1024 * 1024) {
      setError('Video must be under 16MB for WhatsApp.');
      return false;
    }
    if (type.startsWith('audio/') && size > 16 * 1024 * 1024) {
      setError('Audio must be under 16MB for WhatsApp.');
      return false;
    }
    if (size > 100 * 1024 * 1024) {
      setError('File must be under 100MB.');
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (validateFile(selected)) {
      setFile(selected);
      if (!displayName) setDisplayName(selected.name);
      
      // Auto-assign category
      if (selected.type.startsWith('image/')) setCategory('Image');
      else if (selected.type.startsWith('video/')) setCategory('Video');
      else if (selected.type.startsWith('audio/')) setCategory('Audio');
      else setCategory('Document');
    } else {
      e.target.value = null;
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please select a file.');
      return;
    }

    setIsUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('displayName', displayName);
    formData.append('category', category || 'Other');

    try {
      const res = await api('/media-library/upload', {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || 'Upload failed');
      }

      const newFile = await res.json();
      onUploadSuccess(newFile);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '500px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Upload Media</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
            <AlertCircle size={18} /> {error}
          </div>
        )}

        <form onSubmit={handleUpload}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Select File</label>
            <div style={{ border: '2px dashed var(--color-border-primary)', borderRadius: '8px', padding: '32px', textAlign: 'center', background: 'var(--color-background-primary)', cursor: 'pointer', position: 'relative' }}>
              <input 
                type="file" 
                onChange={handleFileChange}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
              />
              {file ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <File size={32} color="var(--color-brand-primary)" />
                  <span style={{ fontWeight: '500', color: 'var(--color-text-primary)' }}>{file.name}</span>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: 'var(--color-text-secondary)' }}>
                  <UploadCloud size={32} />
                  <span>Click or drag file to upload</span>
                  <span style={{ fontSize: '12px' }}>Max: Image (5MB), Video (16MB), Doc (100MB)</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>Display Name</label>
            <input 
              type="text" 
              value={displayName} 
              onChange={e => setDisplayName(e.target.value)} 
              placeholder="e.g. Q3 Brochure"
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>Category</label>
            <select 
              value={category} 
              onChange={e => setCategory(e.target.value)} 
              style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }}
            >
              <option value="" disabled>Select a category...</option>
              {predefinedCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button 
              type="button" 
              onClick={onClose}
              style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', background: 'white', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isUploading || !file}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '6px', border: 'none', background: 'var(--color-brand-primary)', color: 'white', cursor: (isUploading || !file) ? 'not-allowed' : 'pointer', opacity: (isUploading || !file) ? 0.7 : 1 }}
            >
              {isUploading ? 'Uploading...' : 'Upload File'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MediaUploadModal;
